// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Badge, Button } from '@mui/material';
import { AppIcon, SearchBar, Tabs } from '@shared/ui-kit';
import { Translation } from '@shared/ui-kit/providers/translation-provider';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import type { DocumentScopeTab } from '../../types/document-filters';
import { useDocumentsToolbar } from './use-documents-toolbar';

export const DocumentsToolbar = defineComponent({
  displayName: 'DocumentsToolbar',
  useSetup: useDocumentsToolbar,
  View: ({
    t,
    scope,
    onScopeChange,
    search,
    onSearchChange,
    activeFilterCount,
    onOpenFilters,
    disabled,
    hideScopeTabs,
  }) => (
    <div
      className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-4"
      data-testid="documents-toolbar"
    >
      {hideScopeTabs ? null : (
        <Tabs.Root
          baseId="documents-scope-tabs"
          defaultValue={scope}
          controlled
          onTabChange={value => onScopeChange(value as DocumentScopeTab)}
          variant="segmented"
          size="sm"
          className="shrink-0"
        >
          <Tabs.List>
            <Tabs.Trigger value="all" disabled={disabled}>
              <Translation i18nKey="documents.scope.all" ns="components" />
            </Tabs.Trigger>
            <Tabs.Trigger value="personal" disabled={disabled}>
              <Translation i18nKey="documents.scope.personal" ns="components" />
            </Tabs.Trigger>
            <Tabs.Trigger value="company" disabled={disabled}>
              <Translation i18nKey="documents.scope.company" ns="components" />
            </Tabs.Trigger>
            <Tabs.Trigger value="silver_alpha" disabled={disabled}>
              <Translation i18nKey="documents.scope.silver_alpha" ns="components" />
            </Tabs.Trigger>
          </Tabs.List>
        </Tabs.Root>
      )}

      <div className="flex flex-col gap-3 lg:min-w-0 lg:flex-1 lg:flex-row lg:items-center lg:justify-end lg:gap-4">
        <div className="w-full lg:min-w-0 lg:max-w-md lg:flex-1">
          <SearchBar
            placeholder={t('documents.search-placeholder', { ns: 'components' })}
            value={search}
            onChangeValue={onSearchChange}
            dataTestId="documents-search-bar"
            disabled={disabled}
          />
        </div>

        <Badge
          badgeContent={activeFilterCount}
          color="secondary"
          invisible={activeFilterCount === 0}
          className="w-full lg:w-auto"
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={onOpenFilters}
            disabled={disabled}
            startIcon={<AppIcon icon="Funnel" size="base" color="inherit" />}
            className="h-10 w-full shrink-0 normal-case lg:w-auto"
            data-testid="documents-filters-button"
          >
            <Translation i18nKey="documents.filters" ns="components" />
          </Button>
        </Badge>
      </div>
    </div>
  ),
});
